import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { PROCESS_STEPS, BRAND_INFO } from '../data/mock';

const HowItWorksPage = () => {
  usePageTitle(
    `How it works | ${BRAND_INFO.name}`,
    'From order to first result in under 48 hours. Pick a panel or build à la carte, get a physician-signed requisition, walk in to a partner lab, and watch results stream into your dashboard.'
  );

  return (
    <>
      <Header />

      <section className="pt-28 lg:pt-36 pb-16 bg-cream-100">
        <div className="container-wide">
          <p className="eyebrow">How it works</p>
          <h1 className="mt-5 font-display text-display-lg text-ink-900 max-w-3xl">
            From order to first result in{' '}
            <em className="italic text-brick-500">under 48 hours</em>.
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl">
            No clinic visits. No portal hopping. No mysterious envelopes in the mail. The
            whole flow from “I want to know” to “markers in my dashboard” takes a couple of
            days at most.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-cream-100">
        <div className="container-wide grid lg:grid-cols-2 gap-6">
          {PROCESS_STEPS.map((s) => (
            <article
              key={s.step}
              className="bg-cream-50 border border-cream-200 rounded-3xl p-8"
            >
              <div className="flex items-baseline gap-5">
                <div className="font-display text-5xl text-brick-500 leading-none">{s.step}</div>
                <h3 className="font-display text-2xl text-ink-900">{s.title}</h3>
              </div>
              <p className="mt-5 text-ink-700 leading-relaxed text-[15px]">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20 bg-charcoal-900 text-cream-50">
        <div className="container-wide grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-display-md">
              No clinic visit. No referral.{' '}
              <em className="italic text-brick-300">Just labs.</em>
            </h2>
            <p className="mt-4 text-cream-200/80 max-w-2xl">
              Each requisition is reviewed and signed by a licensed independent physician on
              our partner network as part of your order. You get drawn at any participating
              Quest, LabCorp, or BioReference draw center.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/panels"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brick-500 hover:bg-brick-600 text-white"
            >
              Start your panel <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default HowItWorksPage;
